import type { RenderMode } from '@seamless/oneweb-ssr';
import type { PageEnvironmentVariables } from '@seamless/one-context';

///////////////////
// Vehicle Model //
///////////////////

export interface DataPoint {
  value?: number;
  unit?: string;
  min?: number;
  max?: number;
}

export interface VehicleDimensions {
  length: DataPoint | null;
  height: DataPoint | null;
  width: DataPoint | null;
  widthWithRetractedMirrors: DataPoint | null;
  wheelBase: DataPoint | null;
}

export interface LoadingSpaceDimensions {
  lengthMax: DataPoint | null;
  heightMax: DataPoint | null;
  widthMax: DataPoint | null;
  widthMin: DataPoint | null;
  closed: DataPoint | null;
  bootCapacity: DataPoint | null;
}

export interface Weights {
  payloadMax: DataPoint | null;
  kerbWeight: DataPoint | null;
  weightMax: DataPoint | null;
  permissibleWeightTotal: DataPoint | null;
  trailingLoadWithBrakesMax: DataPoint | null;
  trailingLoadWithoutBrakesMax: DataPoint | null;
}

export interface Body {
  doors: DataPoint | null;
  seats: DataPoint | null;
}

export interface ImagesData {
  front?: string;
  rear?: string;
  side?: string;
}

export interface Groups {
  vehicleDimensions: VehicleDimensions;
  loadingSpaceDimensions: LoadingSpaceDimensions;
  weights: Weights;
  body: Body;
}

export interface TechDataVehicle {
  sizeType: SizeTypeLabel;
  batteryCapacity: number;
  cabinType: CabinTypeLabel | null;
  drivetrainType: DrivetrainLabel | null;
  roofHeightType: RoofTypeLabel | null;
  engineType: EngineTypeLabel | null;
  vehicleDimensions?: VehicleDimensions;
  loadingSpaceDimensions?: LoadingSpaceDimensions;
  weights?: Weights;
  body?: Body;
  imagesData: ImagesData;
  vehicleClass: string;
  baumuster: string;
  bodyType: string;
}

//////////////////////
// Variations model //
//////////////////////

export interface WeightMax {
  drivetrainTypes: DrivetrainTypesLabel[];
  weightMax: number;
}

interface CabinType {
  cabinType: CabinTypeLabel;
  weightsMax: WeightMax[];
}

interface RoofType {
  roofHeightType: RoofTypeLabel;
  weightsMax: WeightMax[];
}

export interface CabinVariations {
  sizeType: SizeTypeLabel;
  cabinTypes: CabinType[];
}

export interface RoofVariations {
  sizeType: SizeTypeLabel;
  roofHeightTypes: RoofType[];
}

export interface SizeEngineVariations {
  sizeType: SizeTypeLabel;
  engineTypes: EngineTypesLabel[];
}
export interface EngineTypesLabel {
  engineType: EngineTypeLabel;
  batteryCapacities: number[];
}

export interface SizeVariations {
  sizeType: SizeTypeLabel;
  weightsMax: WeightMax[];
}

export interface TechDataVariations {
  buttonGroups: ButtonGroup[];
  variations:
    | CabinVariations[]
    | RoofVariations[]
    | SizeEngineVariations[]
    | SizeVariations[];
}

export interface DrivetrainTypesLabel {
  drivetrainType: DrivetrainLabel;
  engineTypes: EngineTypesLabel[];
}
export enum ButtonGroup {
  CABIN = 'CABIN',
  ENGINE = 'ENGINE',
  ROOF = 'ROOF',
  SIZE = 'SIZE',
  WEIGHT_MAX = 'WEIGHT_MAX',
  DRIVETRAIN = 'DRIVETRAIN',
  BATTERY_CAPACITY = 'BATTERY_CAPACITY',
}

export enum SizeTypeLabel {
  COMPACT = 'owvc_tech_data_sizeType_compact',
  STANDARD = 'owvc_tech_data_sizeType_standard',
  LONG = 'owvc_tech_data_sizeType_long',
  EXTRA_LONG = 'owvc_tech_data_sizeType_extraLong',
}

export enum RoofTypeLabel {
  NORMAL = 'owvc_tech_data_roofType_normal',
  HIGH = 'owvc_tech_data_roofType_high',
  SUPER_HIGH = 'owvc_tech_data_roofType_superHigh',
}

export enum CabinTypeLabel {
  SINGLE = 'owvc_tech_data_cabinType_single',
  DOUBLE = 'owvc_tech_data_cabinType_double',
}

export enum EngineTypeLabel {
  DIESEL = 'owvc_tech_data_engineType_diesel',
  ELETRIC = 'owvc_tech_data_engineType_eletric',
  PETROL = 'owvc_tech_data_engineType_petrol',
}

export enum DrivetrainLabel {
  AWD = 'owvc_tech_data_driveTrain_awd',
  FWD = 'owvc_tech_data_driveTrain_fwd',
  RWD = 'owvc_tech_data_driveTrain_rwd',
}

type ConnectionAemEnvironmentState = PageEnvironmentVariables;

export type PartialAemEnvState = Pick<
  ConnectionAemEnvironmentState,
  'country' | 'language' | 'vehicleData' | 'stage' | 'title'
>;

export type Environment = 'TEST' | 'INT' | 'PPROD' | 'PROD';

export type AemData<T = unknown> = T & {
  stage: Environment;
  isAemEditMode: boolean;
  url: string;
  renderMode: RenderMode;
  country: string;
  language: string;
  modelSeries: string;
  title?: string;
};
